import type { PropFootprint } from "../compile/footprint.ts";
import { FOOTPRINT_DEFAULT_CELL } from "../compile/footprint-cell.ts";
import { footprintAfterChange, type FootprintOverride } from "../compile/footprint-override.ts";
import { standMarksIssue } from "../compile/stand-cells.ts";
import type { FrameMarks } from "../compile/stand-map.ts";
import type { FootprintDrafts, FootprintDraftState, FootprintWrite } from "./footprint-drafts.ts";
import { isStaleFootprint, type FootprintSaver } from "./footprint-stale.ts";

/**
 * Writing a frame's footprint to the pack's library and patching the editor to match (ADR-040, ADR-070's
 * amendment of 2026-10-04, P1.6-T89), apart from any panel: the footprint panel's Save runs these while it
 * shows the frame, and a draft left pending on a frame no panel shows is written by them when the page saves.
 */

/**
 * What a write reads of the editor: its footprints, read and then patched once the library holds the write, the
 * footprint the build derives for a frame, and the record of drafts and writes. `EditorStore` is one.
 */
export interface FootprintLibraryView {
  readonly footprintDrafts: FootprintDrafts;
  readonly footprintOf: (frameId: string) => PropFootprint | undefined;
  readonly setFootprint: (frameId: string, footprint: PropFootprint | null) => void;
  readonly derivedFootprintOf: (frameId: string) => PropFootprint | undefined;
  readonly marksOf?: (frameId: string) => FrameMarks | undefined;
  readonly libraryMarksOf?: (frameId: string) => FrameMarks | undefined;
  readonly setMarks?: (frameId: string, marks: FrameMarks | null) => void;
}

/**
 * One frame's write: the editor, the frame and its pack, the frame's pixel size, the collision cell's size in
 * pixels, and the host's saver, which throws when the library refuses the write. `writeBase`, where the host can
 * write a frame's sort line (ADR-132 decision 13), keeps a row counted down from the frame's top in frame pixels and
 * throws when it is refused.
 */
export interface FootprintWriteTarget {
  readonly view: FootprintLibraryView;
  readonly frame: string;
  readonly packId: string;
  readonly size: { readonly w: number; readonly h: number };
  readonly cellPixels: number;
  readonly onSave: FootprintSaver;
  readonly writeBase?: (row: number) => Promise<void>;
}

/**
 * The writes a draft makes, in order. A chosen Auto drops the shape override, null, and then writes the ground
 * flag when the box was changed, since dropping the shape keeps the flag the frame carried; None and Custom are
 * one override with the flag beside the cells; an untouched mode writes the flag alone, and nothing changed
 * writes nothing. A draft that holds a passage writes it beside the cells, with its surface when it has cells
 * and names one: Custom its own, and None an empty one beside a ground flag, since `none` alone removes a
 * passage already.
 *
 * @param state The draft; a write that carries cells or a passage names the draft's `cell` beside them.
 * @returns The overrides to send, null for the derived footprint.
 */
export function draftOverrides(state: FootprintDraftState): (FootprintOverride | null)[] {
  const marks = markFields(state);
  if (marks === null) {
    return shapeOverrides(state);
  }
  if (state.mode === "auto") {
    return [...shapeOverrides(state), marks];
  }
  return shapeOverrides(state, marks);
}

/**
 * The fields a draft's marks make, which travel in the override that holds the cells so the library never sees the
 * lists at two cell sizes (`cell` is named beside them whenever it is not the authoring cell).
 *
 * @param state The draft.
 * @returns The `behind` and `front` lists and the cell, or null when the draft holds no marks part. Empty lists are
 *   sent as they are: the library takes them as removing the marks.
 */
function markFields(state: FootprintDraftState): { cell?: number; behind: [number, number][]; front: [number, number][] } | null {
  if (state.stand === undefined) {
    return null;
  }
  return { ...sizeOf(state), behind: cellsOf(state.stand.behind), front: cellsOf(state.stand.front) };
}

/**
 * Cells as the pairs an override holds.
 *
 * @param keys `column,rowsUp` keys.
 * @returns The cells.
 */
function cellsOf(keys: readonly string[]): [number, number][] {
  return keys.map((key) => key.split(",").map(Number) as [number, number]);
}

/**
 * The cell a draft's lists are named at: nothing at the authoring cell.
 *
 * @param state The draft.
 * @returns `{ cell }` or nothing.
 */
function sizeOf(state: FootprintDraftState): { cell?: number } {
  return state.cell === undefined || state.cell === FOOTPRINT_DEFAULT_CELL ? {} : { cell: state.cell };
}

/**
 * The writes of a draft's footprint, ground flag and the mark beside it.
 *
 * @param state The draft.
 * @param marks The marks fields to send in the same override as the cells, or none.
 * @returns The overrides to send, null for the derived footprint.
 */
function shapeOverrides(state: FootprintDraftState, marks: object = {}): (FootprintOverride | null)[] {
  const groundPart = { ...(state.ground === null ? {} : { ground: state.ground }), ...(state.amongProps === undefined || state.amongProps === null ? {} : { amongProps: state.amongProps }) };
  const flagged = Object.keys(groundPart).length > 0;
  if (state.mode === "auto") {
    return [...(state.touched ? [null] : []), ...(flagged ? [groundPart] : [])];
  }
  const sized = sizeOf(state);
  if (state.mode === "none") {
    return [!flagged && Object.keys(marks).length === 0 ? "none" : { ...sized, cells: [], ...groundPart, ...(state.passage === undefined ? {} : { passage: [] }), ...marks }];
  }
  const surface = state.surface ?? null;
  const passagePart = state.passage === undefined ? {} : { passage: cellsOf(state.passage), ...(state.passage.length > 0 && surface !== null ? { surface } : {}) };
  return [{ ...sized, cells: cellsOf(state.cells), ...groundPart, ...passagePart, ...marks }];
}

/**
 * Sends one override to the library and, once it holds it, patches the editor's footprint for the frame and
 * records the write: the derived footprint for null, and otherwise what the library's merge leaves the frame
 * (`footprintAfterChange`): an empty one for nothing, the cells' for cells with the passage the frame keeps or
 * the change brings, and no change of shape for an override of a flag alone.
 *
 * @param target The frame's write.
 * @param override The override, or null to drop the frame's shape override.
 * @throws Whatever the host's saver throws when the library refuses the write; the editor is then unchanged.
 */
export async function writeFootprintOverride(target: FootprintWriteTarget, override: FootprintOverride | null): Promise<void> {
  const { view, frame, size, cellPixels } = target;
  await target.onSave(target.packId, frame, override);
  if (override !== null && override !== "none" && (override.behind !== undefined || override.front !== undefined)) {
    const behind = override.behind ?? view.libraryMarksOf?.(frame)?.behind ?? view.marksOf?.(frame)?.behind ?? [];
    const front = override.front ?? view.libraryMarksOf?.(frame)?.front ?? view.marksOf?.(frame)?.front ?? [];
    view.setMarks?.(frame, behind.length === 0 && front.length === 0 ? null : { cell: override.cell ?? FOOTPRINT_DEFAULT_CELL, behind, front });
  }
  if (override === null) {
    view.setFootprint(frame, view.derivedFootprintOf(frame) ?? null);
  } else {
    const after = footprintAfterChange(view.footprintOf(frame), override, size, cellPixels);
    if (after !== undefined) {
      view.setFootprint(frame, after);
    }
  }
  view.footprintDrafts.wrote(frame);
}

/**
 * Writes a draft to its own frame, whatever a panel shows, and lets the draft go once every write has landed.
 *
 * @param target The frame's write.
 * @param state The draft.
 * @returns Written once the library holds the draft and its base line; refused with the saver's reason when a write
 *   was refused, the draft then still held.
 */
export async function writeFootprintDraft(target: FootprintWriteTarget, state: FootprintDraftState): Promise<FootprintWrite> {
  const marks = markFields(state);
  const refusal = marks === null ? undefined : standMarksIssue({ ...(state.mode === "custom" ? { cells: cellsOf(state.cells) } : {}), behind: marks.behind, front: marks.front, ...(state.cell === undefined ? {} : { cell: state.cell }) }, { w: target.size.w, h: target.size.h });
  if (refusal !== undefined) {
    return { written: false, reason: refusal };
  }
  if (state.base !== undefined && target.writeBase === undefined) {
    return { written: false, reason: "this frame's base line cannot be written from here" };
  }
  try {
    for (const override of draftOverrides(state)) {
      await writeFootprintOverride(target, override);
    }
    if (state.base !== undefined) {
      await target.writeBase?.(state.base);
    }
  } catch (error) {
    return { written: false, reason: error instanceof Error ? error.message : String(error), ...(isStaleFootprint(error) ? { stale: true as const } : {}) };
  }
  target.view.footprintDrafts.release(target.frame);
  return { written: true };
}
